import { Pencil } from 'lucide-react';
import { Button } from '@@/ui/primitives/button';
import { CampaignType } from '../../types';
import { ScheduleData, ScheduleType } from './schedule-step';
import { ContactListData } from './contact-list-step';
import { WizardStep } from './wizard-stepper';

const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  [CampaignType.SURVEY_VOICE]: 'Voice Survey',
  [CampaignType.NOTIFICATION_VOICE]: 'Voice Notification',
  [CampaignType.SURVEY_SMS]: 'SMS Survey',
  [CampaignType.NOTIFICATION_SMS]: 'SMS Notification'
};

export function ReviewStep(props: {
  campaignName: string;
  campaignType: CampaignType | null;
  scheduleData: ScheduleData;
  contactListData: ContactListData;
  onEditStep: (step: WizardStep) => void;
}) {
  return (
    <div>
      <h2 className='text-xl font-semibold text-foreground mb-6'>Review & Launch</h2>

      <div className='space-y-6'>
        <CampaignNameSection name={props.campaignName} />

        <TypeSection campaignType={props.campaignType} onEdit={() => props.onEditStep(WizardStep.TYPE)} />

        <MessageSequenceSection onEdit={() => props.onEditStep(WizardStep.MESSAGE)} />

        <ScheduleSection data={props.scheduleData} onEdit={() => props.onEditStep(WizardStep.SCHEDULE)} />

        <ContactsSection data={props.contactListData} onEdit={() => props.onEditStep(WizardStep.CONTACT_LIST)} />
      </div>
    </div>
  );
}

function CampaignNameSection(props: { name: string }) {
  return (
    <div>
      <span className='text-sm text-muted-foreground'>Campaign Name: </span>
      <span className='text-sm font-medium text-foreground'>{props.name || 'Untitled Campaign'}</span>
    </div>
  );
}

function TypeSection(props: { campaignType: CampaignType | null; onEdit: () => void }) {
  return (
    <ReviewSection title='Type' onEdit={props.onEdit} editLabel='Edit Campaign Type'>
      <div>
        <p className='text-xs font-medium text-muted-foreground mb-2'>Campaign type</p>
        <div className='bg-muted rounded-lg px-4 py-3'>
          <span className='text-sm text-muted-foreground'>
            {props.campaignType ? CAMPAIGN_TYPE_LABELS[props.campaignType] : 'Not selected'}
          </span>
        </div>
      </div>
    </ReviewSection>
  );
}

function MessageSequenceSection(props: { onEdit: () => void }) {
  return (
    <ReviewSection title='Message Sequence' onEdit={props.onEdit} editLabel='Edit Message'>
      <div className='space-y-4'>
        <MessagePreviewCard index={1} />
        <WaitingTimeCard />
        <MessagePreviewCard index={2} />
        <MessagePreviewCard index={3} />
      </div>
    </ReviewSection>
  );
}

function MessagePreviewCard(props: { index: number }) {
  return (
    <div className='border border-border rounded-lg p-4'>
      <div className='flex gap-6'>
        <div className='flex-1'>
          <p className='text-xs font-medium text-foreground mb-2'>Message {String(props.index).padStart(2, '0')}</p>
          <div className='bg-muted rounded-lg px-4 py-3'>
            <span className='text-sm text-muted-foreground'>Type your message here.</span>
          </div>
          <p className='text-xs text-muted-foreground text-right mt-2'>0/500 Characters</p>
        </div>
        <div>
          <p className='text-xs font-medium text-foreground mb-2'>Preview</p>
          <AudioPreviewPlaceholder />
        </div>
      </div>
    </div>
  );
}

function AudioPreviewPlaceholder() {
  return (
    <div className='flex items-center gap-2 bg-muted rounded-full px-3 py-2'>
      <div
        className='w-8 h-8 rounded-full bg-blue flex items-center
        justify-center'
      >
        <PlayIcon />
      </div>
      <WaveformPlaceholder />
      <span className='text-xs text-muted-foreground'>0:05</span>
      <VolumeIcon />
    </div>
  );
}

function WaitingTimeCard() {
  return (
    <div className='border border-border rounded-lg p-4'>
      <p className='text-xs font-medium text-foreground mb-3'>Waiting Time</p>
      <div className='flex gap-4'>
        <div className='flex-1'>
          <div
            className='bg-muted rounded-lg px-4 py-3 flex items-center
            justify-between'
          >
            <span className='text-sm text-muted-foreground'>Set Duration</span>
            <ChevronUpDownIcon />
          </div>
        </div>
        <div className='flex-1'>
          <div
            className='bg-muted rounded-lg px-4 py-3 flex items-center
            justify-between'
          >
            <span className='text-sm text-muted-foreground'>Days</span>
            <ChevronDownIcon />
          </div>
        </div>
      </div>
    </div>
  );
}

function ScheduleSection(props: { data: ScheduleData; onEdit: () => void }) {
  const startDate = props.data.startDate || 'November 5, 2025';
  const startTime = props.data.startTime || '08:00 AM';
  const endDate = props.data.expirationDate || 'November 5, 2025';
  const endTime = props.data.expirationTime || '08:00 AM';
  const quietHours = props.data.quietHoursTime
    ? `${props.data.quietHoursDate} - ${props.data.quietHoursTime}`
    : '05:01 PM - 07:59 AM';
  const retrySettings =
    props.data.retryFailedCalls === 'none' ? 'No retries' : `${props.data.retryFailedCalls} retry after 2 hours`;

  return (
    <ReviewSection title='Schedule' onEdit={props.onEdit} editLabel='Edit Schedule'>
      <div className='grid grid-cols-2 gap-x-8 gap-y-4'>
        <InfoField
          label='Start Date:'
          value={props.data.scheduleType === ScheduleType.IMMEDIATELY ? 'Immediately' : `${startDate} ${startTime}`}
        />
        <InfoField label='End Date:' value={`${endDate} ${endTime}`} />
        <InfoField label='Quiet Hours:' value={quietHours} />
        <InfoField label='Retry Settings:' value={retrySettings} />
      </div>
    </ReviewSection>
  );
}

function ContactsSection(props: { data: ContactListData; onEdit: () => void }) {
  const fileName = props.data.file?.name || 'Document Name.csv';
  const contactCount = '3,600 contacts';

  return (
    <ReviewSection title='Contacts' onEdit={props.onEdit} editLabel='Replace File'>
      <div>
        <p className='text-xs font-medium text-foreground mb-1'>File Uploaded:</p>
        <p className='text-sm text-foreground'>
          {fileName} | [{contactCount}]
        </p>
      </div>
    </ReviewSection>
  );
}

function ReviewSection(props: { title: string; editLabel: string; onEdit: () => void; children: React.ReactNode }) {
  return (
    <div>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-base font-medium text-foreground'>{props.title}</h3>
        <Button variant='ghost' size='sm' onClick={props.onEdit} className='text-blue hover:text-blue/80 h-auto p-0'>
          <Pencil className='w-3 h-3 mr-1' />
          {props.editLabel}
        </Button>
      </div>
      {props.children}
    </div>
  );
}

function InfoField(props: { label: string; value: string }) {
  return (
    <div>
      <p className='text-xs font-medium text-foreground'>{props.label}</p>
      <p className='text-sm text-muted-foreground'>{props.value}</p>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg className='w-3 h-3 text-white ml-0.5' fill='currentColor' viewBox='0 0 24 24'>
      <path d='M8 5v14l11-7z' />
    </svg>
  );
}

function WaveformPlaceholder() {
  return (
    <div className='flex items-center gap-0.5'>
      {[4, 8, 6, 10, 4, 8, 12, 6, 8, 4, 10, 6, 8, 4].map((h, i) => (
        <div key={i} className='w-0.5 bg-muted-foreground rounded-full' style={{ height: h }} />
      ))}
    </div>
  );
}

function VolumeIcon() {
  return (
    <svg className='w-4 h-4 text-muted-foreground' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0
          010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0
          011-1h1.586l4.707-4.707C10.923 3.663 12 4.109
          12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z'
      />
    </svg>
  );
}

function ChevronUpDownIcon() {
  return (
    <svg className='w-4 h-4 text-muted-foreground' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 9l4-4 4 4m0 6l-4 4-4-4' />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg className='w-4 h-4 text-muted-foreground' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
    </svg>
  );
}
